// railState.ts — the FunctionRail EXPANDED/COLLAPSED state (icons ↔ icons+names).
// CONTRACT: docs/ux/SHELL-COMPOSITION.md §2.1 (the left function rail)
//
// A tiny shared store (heavyPanel.ts pattern) because TWO components need the
// flag: the FunctionRail (renders the toggle + the name labels) and the
// LeftSlideout (shifts its left anchor off the rail's live width so the expanded
// rail never buries an open pane). No host, no MapLibre, no security path — pure
// device-scope UI state.
//
// PERSISTENCE (interim): direct localStorage under the "tak-tdf:" prefix.
// Device-scope, non-identity display state — the same class of preference the
// local-settings store holds. A parallel lane is bumping SETTINGS_SCHEMA_VERSION
// 3→4 in packages/local-settings right now, so this deliberately does NOT touch
// that package; future fold-in: migrate this key to a device-scope local-settings
// leaf once the v4 schema lands.

/** The rail's expanded width (px) — the LeftSlideout anchors to this when the
 *  rail is expanded; collapsed it anchors to the classic 54px column. Kept here
 *  so the two components can never drift. */
export const RAIL_EXPANDED_W = 196;
export const RAIL_COLLAPSED_W = 54;

const STORAGE_KEY = "tak-tdf:rail-expanded";

function readPersisted(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false; // storage denied (private mode etc.) → default collapsed
  }
}

let expanded = readPersisted();
const listeners = new Set<(expanded: boolean) => void>();

/** Current expanded flag. Synchronous read. */
export function isRailExpanded(): boolean {
  return expanded;
}

/** Set + persist the expanded flag. Idempotent (no redundant emit). */
export function setRailExpanded(next: boolean): void {
  if (expanded === next) return;
  expanded = next;
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  } catch {
    // storage denied — the in-memory flag still works for this session.
  }
  for (const fn of [...listeners]) fn(expanded);
}

/** Subscribe to changes. Returns an unsubscribe fn (not invoked on subscribe). */
export function subscribeRail(fn: (expanded: boolean) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// --- the React binding ---------------------------------------------------------

import { useEffect, useState } from "react";

/** Live expanded flag for React consumers (FunctionRail toggle, LeftSlideout shift). */
export function useRailExpanded(): boolean {
  const [value, setValue] = useState(isRailExpanded());
  useEffect(() => {
    setValue(isRailExpanded()); // re-sync across the render→effect gap
    return subscribeRail(setValue);
  }, []);
  return value;
}
